// AUTO-GENERATED by scripts/generate-contracts.mjs from the .md sidecar.
// Hand edits will be overwritten on the next run. To customise this
// component's contract (add actions, structured props, custom control
// kinds, glyph pickers), remove this file's auto-generated marker and
// hand-author it — the generator skips files without the marker.

import { z } from "zod";
import { contract } from "@gradeui/contracts";

export const MapContract = contract({
  name: "Map",
  description: "Any layout that needs a real map — listings (real estate, Airbnb-style), fleet/logistics dashboards, store locators, anywhere a user picks a location from a viewport. Reach for the controlled `hoveredId` prop when a sibling list and the map need to highlight each other.",
  import: "@gradeui/ui",
  aliases: ["map","maps","mapbox","maplibre","google maps","geo","location","latlng","coordinates","marker","pin","airbnb","listings","fleet","real estate","logistics","map view","mapkit","mapview","react native maps","rn maps"],
  subcomponents: ["MapMarker"],
  composesWith: ["Card (as marker content)","Badge","Avatar","Button","Row","Stack","Skeleton"],
  props: {
  "provider": {
      schema: z.unknown(),
      design: "plumbing",
      description: "\"maplibre\" (default, free, no key) | \"mapbox\" (needs accessToken) | \"google\" (needs apiKey). Switching is one prop change.",
  },
  "center": {
      schema: z.unknown(),
      design: "plumbing",
      description: "`[lng, lat]` tuple. ALWAYS lng first. Required.",
  },
  "zoom": {
      schema: z.unknown(),
      design: "plumbing",
      description: "number, 0–22. Required.",
  },
  "bounds": {
      schema: z.unknown(),
      design: "plumbing",
      description: "`[[swLng, swLat], [neLng, neLat]]`. When set, takes precedence over center+zoom.",
  },
  "appearance": {
      schema: z.unknown(),
      design: "plumbing",
      description: "\"light\" | \"dark\" | \"satellite\" | \"auto\" (default \"auto\", follows GradeThemeProvider mode).",
  },
  "hoveredId": {
      schema: z.unknown(),
      design: "plumbing",
      description: "controlled string id, pairs with onHoveredIdChange. The matching MapMarker gets `data-gds-state=\"hovered\"` automatically. This is how you build list ↔ map two-way sync.",
  },
  "onHoveredIdChange": {
      schema: z.unknown(),
      design: "event",
      description: "`(id: string | null) => void`. The other half of the controlled hover pair: fires when the pointer enters/leaves a marker so the sibling list can highlight in step. Without this entry in the contract, screens using the documented two-way sync fail save validation.",
  },
  "interactive": {
      schema: z.unknown(),
      design: "plumbing",
      description: "false freezes pan/zoom, useful for static cards.",
  },
  "tools": {
      schema: z.unknown(),
      design: "plumbing",
      description: "\"auto\" (default, zoom buttons follow `interactive`) | \"zoom\" (force zoom buttons) | \"none\" (chrome-free map; attribution always stays — license). One vocabulary across all providers.",
  },
  "toolsPosition": {
      schema: z.unknown(),
      design: "plumbing",
      description: "\"top-left\" (default) | \"top-right\" | \"bottom-left\" | \"bottom-right\". Corner the tools dock to; use when a search bar or legend sits over the default corner.",
  },
  "onLoad": {
      schema: z.unknown(),
      design: "event",
      description: "handle exposes flyTo, panTo, fitBounds, getCenter, getZoom, getBounds, instance.",
  },
  "tilerKey": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "MapLibre only (provider=\"maplibre\"). Optional everywhere: omit on `gradeui.com`/`localhost` and the referrer-locked demo key is used; set it only when embedding off-domain. The contract never requires it.",
  },
  "accessToken": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "Mapbox only. Pass it whenever provider=\"mapbox\" — the component itself enforces this at runtime (throws a clear `provider=\"mapbox\" requires an accessToken prop` error via onError if missing). It is OPTIONAL in the contract on purpose, so the validator never demands it from maplibre/google maps.",
  },
  "apiKey": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "Google only. Pass it whenever provider=\"google\" — the component enforces it at runtime (throws `provider=\"google\" requires an apiKey prop` via onError if missing). OPTIONAL in the contract on purpose, so it's never demanded from maplibre/mapbox.",
  },
  "id": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "string. Required. Stable marker id; pair with Map's `hoveredId` for list↔map hover sync.",
  },
  "at": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "`[lng, lat]` tuple. Required. THE coordinate prop. ALWAYS lng first. The prop is literally named `at` — it is NOT `lngLat`, `coordinates`, `position`, `latLng`, `center`, or separate `lng`/`lat` props. Passing any other name leaves the marker coord `undefined`, and MapLibre throws on mount, crashing the WHOLE screen in every renderer. When in doubt, copy the `airbnb-listings` scaffold: `<MapMarker id={l.id} at={l.coords}>`.",
  },
  "anchor": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "\"center\" | \"bottom\" (default \"bottom\", pin tip sits on the coord). Only these two values.",
  },
  "onClick": {
      schema: z.unknown().optional(),
      design: "event",
      description: "handler called with `({ id, coords, native })` on marker click.",
  },
  "children": {
      schema: z.unknown().optional(),
      design: "plumbing",
      description: "DOM rendered as the marker (Badge, Card, Avatar, or any element). Inherits `--gds-*` tokens.",
  },
  },
});
